import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import "./index.css";
import pcImg from "../shared/mes2x-pc.webp";
import bgImg from "../assets/AI-Workstation-Hero-BG.webp";

export default function App() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <div className="arsenal-cta-wrapper" ref={containerRef}>
      <motion.div
        className="cta-section"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="cta-content">
          <div className="cta-heading-row">
            <img
              src={pcImg}
              alt="AI Workstation"
              className="cta-mobile-pc"
            />
            <div className="cta-heading-text">
              <div className="cta-badge">AI Workstation</div>
              <h2 className="cta-title">
                <span className="highlight">Next Gen</span> Dual GPU <br />
                AI Workstation
              </h2>
            </div>
          </div>

          <p className="cta-subtitle">
            MES2X Dual Graphics Card Liquid Cooled AI Custom Workstation
          </p>
          <p className="cta-description">
            Multi-GPU RTX workstations with professional liquid cooling and up to 8TB NVMe storage. 
            Optimized for PyTorch, TensorFlow, and CUDA development.
          </p>
          <div className="cta-buttons">
            <a href="https://arsenalpc.com/product/ai-workstations/mes2x-dual-gpu-ai-custom-workstation/" className="btn btn-primary">Customize <span>→</span></a>
            <a href="https://arsenalpc.com/ai-workstations/" className="btn btn-secondary">Learn More</a>
          </div>
        </div>

        <div className="cta-image-wrapper desktop-only">
          <div className="particle-container">
            {/* Floating particles - left side */}
            <motion.div 
              className="tech-particle particle-large"
              initial={{ opacity: 0.3, scale: 0.8 }}
              animate={{ 
                opacity: [0.3, 0.6, 0.3],
                scale: [0.8, 1.2, 0.8],
                x: [-10, 10, -10],
                y: [-8, -20, -8]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              style={{ left: '15%', top: '20%' }}
            />
            <motion.div 
              className="tech-particle particle-medium"
              initial={{ opacity: 0.4, scale: 1 }}
              animate={{ 
                opacity: [0.4, 0.7, 0.4],
                scale: [1, 1.4, 1],
                x: [8, -8, 8],
                y: [5, -15, 5]
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              style={{ left: '8%', top: '65%' }}
            />
            <motion.div 
              className="tech-particle particle-small"
              initial={{ opacity: 0.5, scale: 0.6 }}
              animate={{ 
                opacity: [0.5, 0.8, 0.5],
                x: [-5, 15, -5],
                y: [-12, 8, -12]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              style={{ left: '25%', top: '45%' }}
            />
            
            {/* Floating particles - right side */}
            <motion.div 
              className="tech-particle particle-medium"
              initial={{ opacity: 0.3, scale: 0.9 }}
              animate={{ 
                opacity: [0.3, 0.65, 0.3],
                scale: [0.9, 1.3, 0.9],
                x: [12, -8, 12],
                y: [-6, -18, -6]
              }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              style={{ right: '18%', top: '15%' }}
            />
            <motion.div 
              className="tech-particle particle-large"
              initial={{ opacity: 0.4, scale: 1.1 }}
              animate={{ 
                opacity: [0.4, 0.7, 0.4],
                scale: [1.1, 0.7, 1.1],
                x: [-15, 8, -15],
                y: [10, -12, 10]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 3 }}
              style={{ right: '12%', top: '70%' }}
            />
            <motion.div 
              className="tech-particle particle-small"
              initial={{ opacity: 0.6, scale: 0.5 }}
              animate={{ 
                opacity: [0.6, 0.9, 0.6],
                x: [6, -12, 6],
                y: [-8, 6, -8]
              }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
              style={{ right: '30%', top: '40%' }}
            />
            
            {/* Circuit lines with framer-motion */}
            <motion.div 
              className="circuit-line"
              initial={{ opacity: 0.2, scaleX: 0.3 }}
              animate={{ 
                opacity: [0.2, 0.8, 0.2],
                scaleX: [0.3, 1, 0.3]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              style={{ left: '20%', top: '30%', width: '50px', transform: 'rotate(25deg)' }}
            />
            <motion.div 
              className="circuit-line"
              initial={{ opacity: 0.3, scaleX: 0.5 }}
              animate={{ 
                opacity: [0.3, 0.9, 0.3],
                scaleX: [0.5, 1, 0.5]
              }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              style={{ right: '25%', top: '55%', width: '40px', transform: 'rotate(-15deg)' }}
            />
            
            {/* Circuit dots */}
            <motion.div 
              className="circuit-dot"
              initial={{ opacity: 0.4, scale: 1 }}
              animate={{ 
                opacity: [0.4, 1, 0.4],
                scale: [1, 1.8, 1]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{ left: '18%', top: '55%' }}
            />
            <motion.div 
              className="circuit-dot"
              initial={{ opacity: 0.5, scale: 0.8 }}
              animate={{ 
                opacity: [0.5, 1, 0.5],
                scale: [0.8, 1.6, 0.8]
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
              style={{ right: '20%', top: '35%' }}
            />
          </div>
          <div className="glow-effect"></div>
          <motion.img 
            src={pcImg} 
            alt="AI Workstation" 
            className="pc-image" 
            style={{ y }}
          />
        </div>
      </motion.div>
    </div>
  );
}